import React, { useState } from 'react';
import { claimAddress } from '../services/apiService';
import { FaWallet, FaSignature, FaUserTag, FaLink } from 'react-icons/fa';
import { MdOutlineHelpOutline } from 'react-icons/md';
import useTitle from '../hooks/useTitle';

const ClaimAddressPage = () => {
  useTitle("MENELIK - Claim Address");
  const [address, setAddress] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [signature, setSignature] = useState('');
  const [url, setUrl] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponseMessage('');
    setErrorMessage('');
    setIsLoading(true);

    try {
      const response = await claimAddress(address, displayName, signature, url);
      setResponseMessage(response.message || 'Address claimed successfully!');
    } catch (error) {
      setErrorMessage(error || 'An error occurred while claiming the address.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 px-4 py-6">
      <div className="w-full max-w-lg md:max-w-3xl bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
          <FaUserTag className="text-blue-500 mr-2" />
          Claim Your Address
        </h1>

        <p className="text-gray-600 mb-4 text-center">
          Use the <strong>"Sign Message"</strong> feature in your wallet to verify ownership of your address.
        </p>

        <button
          onClick={() => setShowTutorial(!showTutorial)}
          className="w-full mb-4 bg-gray-100 text-blue-500 px-3 py-2 rounded-lg font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
        >
          {showTutorial ? 'Hide Tutorial' : 'Show Tutorial'}
        </button>

        {showTutorial && (
          <div className="mb-6 bg-gray-50 border rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <MdOutlineHelpOutline className="text-blue-500 mr-2" />
              How to Claim Your Address:
            </h2>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li className="flex items-center">
                <FaWallet className="text-blue-500 mr-2" />
                Open your wallet and navigate to <strong>"Sign Message"</strong>.
              </li>
              <li className="flex items-center">
                <FaSignature className="text-blue-500 mr-2" />
                Enter your address and display name.
              </li>
              <li>Click <strong>"Sign Message"</strong> and copy the signature.</li>
              <li>Paste the signature below and submit the form.</li>
            </ol>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="address" className="block text-gray-700 font-medium mb-2">
              <FaWallet className="inline-block text-blue-500 mr-2" />
              Wallet Address
            </label>
            <input
              type="text"
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="Enter your wallet address"
              required
            />
          </div>

          <div>
            <label htmlFor="displayName" className="block text-gray-700 font-medium mb-2">
              <FaUserTag className="inline-block text-blue-500 mr-2" />
              Display Name
            </label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="Enter your display name"
              required
            />
          </div>

          <div>
            <label htmlFor="signature" className="block text-gray-700 font-medium mb-2">
              <FaSignature className="inline-block text-blue-500 mr-2" />
              Signature
            </label>
            <textarea
              id="signature"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="Paste your signed message"
              required
            />
          </div>

          <div>
            <label htmlFor="url" className="block text-gray-700 font-medium mb-2">
              <FaLink className="inline-block text-blue-500 mr-2" />
              Website URL (Optional)
            </label>
            <input
              type="url"
              id="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="Enter your website URL (optional)"
            />
          </div>

          {isLoading && (
            <div className="text-blue-500 text-center">
              <p>Submitting...</p>
            </div>
          )}

          {responseMessage && (
            <div className="text-green-500 text-center">
              <p>{responseMessage}</p>
            </div>
          )}

          {errorMessage && (
            <div className="text-red-500 text-center">
              <p>{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-blue-500 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition disabled:bg-blue-300"
            disabled={isLoading}
          >
            Claim Address
          </button>
        </form>
      </div>
    </div>
  );
};

export default ClaimAddressPage;
